import { motion, useMotionValue, useTransform, useReducedMotion } from "framer-motion";
import { Globe, AppWindow, Sparkles, Smartphone, Zap, TrendingUp, ShieldCheck } from "lucide-react";
import { LinkButton } from "@/components/Button";
import CrmDashboard from "@/components/mockups/CrmDashboard";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskedLine = ({ index, children, className = "" }) => (
  <span className={`block overflow-hidden pb-1 ${className}`}>
    <motion.span
      className="block will-change-transform"
      custom={index}
      variants={lineVariants}
      initial="hidden"
      animate="visible"
    >
      {children}
    </motion.span>
  </span>
);

const supportPoints = [
  { icon: Zap, label: "Modern Solutions" },
  { icon: TrendingUp, label: "Business Growth" },
  { icon: ShieldCheck, label: "Reliable Partnership" },
];

const floatCards = [
  { icon: Globe, label: "Website Development", cls: "left-[-8%] top-[6%]", depth: 22, anim: "animate-float" },
  { icon: AppWindow, label: "Web Applications", cls: "right-[-6%] top-[22%]", depth: -18, anim: "animate-float-slow" },
  { icon: Sparkles, label: "AI Application Development", cls: "left-[-4%] bottom-[16%]", depth: -24, anim: "animate-float-slow" },
  { icon: Smartphone, label: "Mobile Applications", cls: "right-[-3%] bottom-[6%]", depth: 16, anim: "animate-float" },
];

const Hero = () => {
  const reduce = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const onMouseMove = (e) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 2);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 2);
  };

  const deviceX = useTransform(mx, (v) => v * 10);
  const deviceY = useTransform(my, (v) => v * 8);

  return (
    <section
      className="grid-bg relative overflow-hidden bg-gradient-to-b from-white via-kryvo-mist/60 to-kryvo-mist pt-[72px]"
      data-testid="hero-section"
      onMouseMove={onMouseMove}
    >
      <div
        className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-kryvo-lime/25 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -left-40 top-1/3 h-[420px] w-[420px] rounded-full bg-kryvo-green/10 blur-3xl"
        aria-hidden="true"
      />

      <div className="container-x relative grid items-center gap-14 pb-16 pt-12 sm:pt-16 lg:grid-cols-2 lg:gap-8 lg:pb-24 lg:pt-20">
        {/* Copy */}
        <div className="max-w-xl">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass-card mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-kryvo-deep"
            data-testid="hero-badge"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-kryvo-lime" aria-hidden="true" />
            Digital Product Development &amp; SaaS
          </motion.span>

          <h1 className="text-balance font-display text-4xl font-semibold leading-[1.06] text-kryvo-ink sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            <MaskedLine index={0}>We Build</MaskedLine>
            <MaskedLine index={1}>
              <span className="text-kryvo-green">
                Digital Products
                <svg className="mt-1 block w-full" viewBox="0 0 300 10" preserveAspectRatio="none" aria-hidden="true">
                  <path d="M2 8C60 2 180 2 298 6" stroke="#7ED957" strokeWidth="4" strokeLinecap="round" fill="none" />
                </svg>
              </span>
            </MaskedLine>
            <MaskedLine index={2}>That Move Businesses</MaskedLine>
            <MaskedLine index={3}>Forward.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-6 max-w-lg text-base leading-relaxed text-kryvo-ink/65 sm:text-lg"
            data-testid="hero-subtext"
          >
            From websites and web applications to AI-powered MVPs and mobile apps, Kryvo designs and
            builds technology that turns business ideas into real products.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <LinkButton to="/contact" variant="primary" testId="hero-primary-cta">
              Let's Talk
            </LinkButton>
            <LinkButton to="/services" variant="outline" arrow={false} testId="hero-secondary-cta">
              Explore Our Services
            </LinkButton>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3"
            data-testid="hero-support-points"
          >
            {supportPoints.map((p) => (
              <li key={p.label} className="flex items-center gap-2 text-sm font-medium text-kryvo-ink/70">
                <p.icon className="h-4 w-4 text-kryvo-green" aria-hidden="true" />
                {p.label}
              </li>
            ))}
          </motion.ul>
        </div>

        {/* Visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-xl"
        >
          <motion.div style={reduce ? undefined : { x: deviceX, y: deviceY }}>
            {/* Browser frame */}
            <div className="overflow-hidden rounded-2xl border border-kryvo-green/10 bg-white shadow-[0_40px_90px_-40px_rgba(6,78,42,0.45)]">
              <div className="flex items-center gap-1.5 border-b border-kryvo-green/10 bg-kryvo-mist/70 px-4 py-2.5">
                <span className="h-2.5 w-2.5 rounded-full bg-kryvo-green/20" />
                <span className="h-2.5 w-2.5 rounded-full bg-kryvo-green/30" />
                <span className="h-2.5 w-2.5 rounded-full bg-kryvo-lime" />
                <span className="ml-3 flex-1 truncate rounded-md bg-white px-3 py-1 text-[10px] text-kryvo-ink/45">
                  app.kryvocrm.com/dashboard
                </span>
              </div>
              <div className="aspect-[4/3] p-3 sm:p-4">
                <CrmDashboard compact />
              </div>
            </div>
          </motion.div>

          {/* Floating service cards */}
          {floatCards.map((c, i) => (
            <FloatingCard key={c.label} card={c} mx={mx} my={my} reduce={reduce} index={i} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const FloatingCard = ({ card, mx, my, reduce, index }) => {
  const x = useTransform(mx, (v) => v * card.depth);
  const y = useTransform(my, (v) => v * card.depth * 0.6);
  const Icon = card.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.9 + index * 0.12 }}
      style={reduce ? undefined : { x, y }}
      className={`absolute ${card.cls} hidden md:block`}
    >
      <div className={`glass-card flex items-center gap-2.5 rounded-xl px-4 py-3 ${card.anim}`}>
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-kryvo-soft text-kryvo-green">
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        <span className="whitespace-nowrap text-xs font-semibold text-kryvo-deep">{card.label}</span>
      </div>
    </motion.div>
  );
};

export default Hero;
